import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { Loader2 } from "lucide-react";

const CUSTOM = "__custom__";

export function ScadenzaEditor({ open, onOpenChange, entityType, entityId, initial, onSaved }) {
  const [tipi, setTipi] = useState([]);
  const [form, setForm] = useState({});
  const [customTipo, setCustomTipo] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/scadenze/tipi`, { params: { entity_type: entityType } }).then((r) => setTipi(r.data));
    const f = {
      tipo: initial?.tipo || "",
      descrizione: initial?.descrizione || "",
      data_scadenza: initial?.data_scadenza || "",
      periodicita_mesi: initial?.periodicita_mesi ?? "",
      costo_ultimo_rinnovo: initial?.costo_ultimo_rinnovo ?? "",
      documento_url: initial?.documento_url || "",
      note: initial?.note || "",
    };
    setForm(f);
    setCustomTipo(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tipo || !form.data_scadenza) {
      toast.error("Compilare tipo e data di scadenza");
      return;
    }
    const payload = {
      ...form,
      entity_type: entityType,
      entity_id: entityId,
      periodicita_mesi: form.periodicita_mesi === "" ? null : Number(form.periodicita_mesi),
      costo_ultimo_rinnovo:
        form.costo_ultimo_rinnovo === "" ? null : Number(form.costo_ultimo_rinnovo),
    };
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/scadenze/${initial.id}`, payload);
      else await api.post(`/scadenze`, payload);
      toast.success("Scadenza salvata");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="scadenza-form">
        <DialogHeader>
          <DialogTitle className="font-head">
            {initial?.id ? "Modifica scadenza" : "Nuova scadenza"}
          </DialogTitle>
          <DialogDescription>
            Registra tipo, data di scadenza ed eventuale periodicità e costo.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Tipo scadenza *</Label>
            {customTipo ? (
              <Input
                className="mt-1"
                value={form.tipo}
                onChange={(e) => set("tipo", e.target.value)}
                placeholder="Descrizione tipo personalizzato"
                data-testid="scadenza-tipo-custom"
              />
            ) : (
              <Select
                value={tipi.includes(form.tipo) ? form.tipo : form.tipo ? CUSTOM : ""}
                onValueChange={(v) => {
                  if (v === CUSTOM) {
                    setCustomTipo(true);
                    set("tipo", "");
                  } else set("tipo", v);
                }}
              >
                <SelectTrigger className="mt-1" data-testid="scadenza-tipo">
                  <SelectValue placeholder="Seleziona tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {tipi.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                  <SelectItem value={CUSTOM}>Altro (personalizzato)...</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Data scadenza *</Label>
              <Input
                type="date"
                className="mt-1"
                value={form.data_scadenza}
                onChange={(e) => set("data_scadenza", e.target.value)}
                data-testid="scadenza-data"
              />
            </div>
            <div>
              <Label className="text-xs">Periodicità (mesi)</Label>
              <Input
                type="number"
                className="mt-1"
                value={form.periodicita_mesi}
                onChange={(e) => set("periodicita_mesi", e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Descrizione</Label>
            <Input
              className="mt-1"
              value={form.descrizione}
              onChange={(e) => set("descrizione", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Costo ultimo rinnovo (€)</Label>
              <Input
                type="number"
                step="any"
                className="mt-1"
                value={form.costo_ultimo_rinnovo}
                onChange={(e) => set("costo_ultimo_rinnovo", e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Link documento</Label>
              <Input
                className="mt-1"
                value={form.documento_url}
                onChange={(e) => set("documento_url", e.target.value)}
                placeholder="URL documento (opz.)"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Note</Label>
            <Textarea
              className="mt-1"
              rows={2}
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={saving} data-testid="scadenza-submit">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RinnovaDialog({ open, onOpenChange, scadenza, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open)
      setForm({ data_scadenza: "", costo_ultimo_rinnovo: "", documento_url: "", note: "" });
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.data_scadenza) {
      toast.error("Indicare la nuova data di scadenza");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/scadenze/${scadenza.id}/rinnova`, {
        ...form,
        costo_ultimo_rinnovo:
          form.costo_ultimo_rinnovo === "" ? null : Number(form.costo_ultimo_rinnovo),
      });
      toast.success("Scadenza rinnovata, versione precedente archiviata");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rinnova-form">
        <DialogHeader>
          <DialogTitle className="font-head">Rinnova scadenza</DialogTitle>
          <DialogDescription>
            {scadenza?.tipo} · scadenza attuale {formatDate(scadenza?.data_scadenza)}. La
            versione corrente viene archiviata nello storico.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Nuova data scadenza *</Label>
            <Input
              type="date"
              className="mt-1"
              value={form.data_scadenza}
              onChange={(e) => set("data_scadenza", e.target.value)}
              data-testid="rinnova-data"
            />
          </div>
          <div>
            <Label className="text-xs">Costo rinnovo (€)</Label>
            <Input
              type="number"
              step="any"
              className="mt-1"
              value={form.costo_ultimo_rinnovo}
              onChange={(e) => set("costo_ultimo_rinnovo", e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Link documento</Label>
            <Input
              className="mt-1"
              value={form.documento_url}
              onChange={(e) => set("documento_url", e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Note</Label>
            <Textarea
              className="mt-1"
              rows={2}
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={saving} data-testid="rinnova-submit">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Conferma rinnovo
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
